import { ArrowRight, InstagramLogo, Sparkle, WhatsappLogo } from "@phosphor-icons/react";

import { pulseContent } from "../data/pulseContent";
import { FooterSection, MobileBottomBar, SiteHeader } from "./PulseFitnessPage";

const PULSE_INSTAGRAM_LINK = "https://www.instagram.com/pulsefitnesspk";
const UMAIR_WHATSAPP_LINK = "https://wa.me/923333357807";

export const UmairAnsariPage = () => {
  const umair = pulseContent.trainerCategories
    .flatMap((category) => category.trainers)
    .find((trainer) => trainer.name === "Umair Ansari");

  if (!umair) {
    return null;
  }

  return (
    <main className="app-shell" data-testid="umair-ansari-page">
      <SiteHeader navItems={pulseContent.navItems} />

      <section className="section-shell pb-14 pt-32" data-testid="umair-hero-section">
        <div className="shell grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div className="space-y-6">
            <p className="eyebrow" data-testid="umair-eyebrow">Coach spotlight</p>
            <h1 className="hero-title max-w-4xl" data-testid="umair-title">
              Umair Ansari
            </h1>
            <p className="hero-copy" data-testid="umair-subtitle">
              Fitness and Longevity Coach focused on fat loss, behavior change, and movement restoration.
            </p>

            <div className="floating-panel space-y-4 p-6 md:p-8">
              <p className="eyebrow" data-testid="umair-role-eyebrow">Full profile</p>
              <h2 className="text-3xl font-semibold tracking-tight text-[#1A2525]" data-testid="umair-role-title">
                {umair.role}
              </h2>
              <p className="section-copy text-[#1A2525]" data-testid="umair-specialty-copy">
                {umair.specialty}
              </p>
              <p className="section-copy" data-testid="umair-bio-copy">
                {umair.bio}
              </p>

              <div className="flex flex-wrap gap-3" data-testid="umair-contact-links">
                <a
                  className="contact-icon-pill"
                  data-testid="umair-instagram-link"
                  href={umair.instagram}
                  rel="noreferrer"
                  target="_blank"
                >
                  <InstagramLogo className="h-4 w-4" />
                  <span>Umair’s Instagram</span>
                </a>
                <a
                  className="contact-icon-pill"
                  data-testid="umair-whatsapp-link"
                  href={UMAIR_WHATSAPP_LINK}
                  rel="noreferrer"
                  target="_blank"
                >
                  <WhatsappLogo className="h-4 w-4" />
                  <span>WhatsApp Umair Ansari</span>
                </a>
                <a
                  className="contact-icon-pill"
                  data-testid="umair-email-link"
                  href={`mailto:${pulseContent.clubInfo.email}`}
                >
                  <Sparkle className="h-4 w-4" />
                  <span>Email the team</span>
                </a>
              </div>
            </div>
          </div>

          <div className="floating-panel overflow-hidden p-3 md:p-4">
            <div className="trainer-image-wrap trainer-image-wrap-lg rounded-[1.2rem]">
              <img
                alt="Umair Ansari"
                className="trainer-image"
                data-testid="umair-hero-image"
                src={umair.image}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell pt-0" data-testid="umair-gallery-section">
        <div className="shell space-y-8">
          <div className="max-w-3xl space-y-4">
            <p className="eyebrow" data-testid="umair-gallery-eyebrow">Client transformations</p>
            <h2 className="section-title" data-testid="umair-gallery-title">
              Transformation journeys coached by Umair.
            </h2>
            <p className="section-copy" data-testid="umair-gallery-copy">
              A premium gallery of transformation-style progress visuals for Umair’s page. We can replace each one with real client results whenever you’re ready.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {pulseContent.umairTransformationGallery.map((item, index) => (
              <article
                className="floating-panel overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(45,90,90,0.12)]"
                data-testid={`umair-gallery-card-${item.slug}`}
                key={item.slug}
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#E9EBE4]">
                  <img
                    alt={item.title}
                    className="h-full w-full object-cover"
                    data-testid={`umair-gallery-image-${item.slug}`}
                    src={item.image}
                  />
                </div>
                <div className="space-y-2 p-4">
                  <p className="eyebrow" data-testid={`umair-gallery-tag-${item.slug}`}>Transformation spotlight</p>
                  <h3 className="text-xl font-semibold tracking-tight text-[#1A2525]" data-testid={`umair-gallery-card-title-${item.slug}`}>
                    {item.title}
                  </h3>
                  <p className="section-copy text-sm leading-relaxed" data-testid={`umair-gallery-description-${item.slug}`}>
                    {item.description}
                  </p>
                </div>
                <div className="bg-[#111111] px-4 py-3 text-white" data-testid={`umair-gallery-stats-${item.slug}`}>
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <p className="text-2xl font-semibold uppercase tracking-tight" data-testid={`umair-gallery-client-${item.slug}`}>
                        {item.clientName}{item.age ? `, ${item.age}` : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-[#A8D65E]" data-testid={`umair-gallery-result-${item.slug}`}>
                        {item.result}
                      </p>
                      <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-white/65" data-testid={`umair-gallery-duration-${item.slug}`}>
                        {item.duration}
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell pt-0" data-testid="umair-cta-section">
        <div className="shell">
          <div className="floating-panel flex flex-col gap-6 p-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <p className="eyebrow">Ready for your own transformation?</p>
              <p className="section-copy text-[#1A2525]">
                Reach out through Instagram or contact Pulse Fitness to start training with Umair.
              </p>
            </div>
            <a
              className="pulse-button-primary rounded-full px-6 py-4 text-sm"
              data-testid="umair-cta-instagram-button"
              href={PULSE_INSTAGRAM_LINK}
              rel="noreferrer"
              target="_blank"
            >
              Explore Pulse Fitness
              <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      <MobileBottomBar />
      <FooterSection />
    </main>
  );
};